import React, { useEffect } from "react";
import { parse } from "query-string";

import { Form } from "@sainsburys-tech/luna-form";

import { BLACK_BOX_ELEMENT_ID, reportError } from "@jsainsburyplc/id-ui-common";

import {
  CHALLENGE,
  USERNAME,
  PASSWORD,
  LOGIN_AFTER_REGISTRATION_FORM_ID,
  FROM_REGISTRATION,
} from "../../../constants/login";
import useCookies from "../../../hooks/useCookies";
import { getLoginLink, missionId } from "../../../utils/urls";
import { isIovationEnabled } from "../../../utils/env";
import { initiateOAuth2Flow } from "../../../utils/initiateOAuth2";

const isIovationEnabledValue = isIovationEnabled();

/**
 * sessionStorage key used to carry credentials across the OAuth2 round-trip
 * that SilentLogin triggers when there is no fresh login_challenge (i.e. the
 * user started the registration journey from a direct link rather than via the
 * login page).
 *
 * The key is exported so LoginPage can read it after Hydra redirects back with
 * a fresh challenge.
 */
export const PENDING_REG_LOGIN_KEY = "pending_registration_login";

const getAndAssignBlackBoxToken = (form) => {
  try {
    const bb = window.IGLOO.getBlackbox();
    if (bb && bb.finished) {
      form.elements[BLACK_BOX_ELEMENT_ID].value = bb.blackbox;
    } else {
      reportError("Silent Login - the blackbox is not present!");
    }
  } catch (e) {
    reportError(
      `Silent Login - error occurred during getAndAssignBlackBoxToken: ${e}`
    );
  }
};

export const SilentLogin = ({ username, password, isFromRegistration }) => {
  const { loginChallenge: loginChallengeFromCookie } = useCookies();

  // A challenge coming directly from the URL (Hydra redirect) is always fresh.
  // We prefer it over the cookie which may hold a stale value from a previous
  // session.
  const freshChallengeInUrl = parse(window.location.search)[CHALLENGE];

  // A cookie challenge is only safe to use when it was set during THIS browser
  // session (same tab).  sessionStorage is cleared when the tab closes, so a
  // challenge stored in a previous session will never have this flag.
  // Without this check, a stale challenge (days/weeks old, past Hydra's
  // LOGIN_TIMEOUT) would be used, causing `verifyLogin` inside `send-mfa` to
  // receive a 410 Gone from Hydra and return a 400 error to the SPA.
  const challengeSetInSession =
    sessionStorage.getItem("login_challenge_set_in_session") === "true";
  const canUseCookieChallenge =
    loginChallengeFromCookie && challengeSetInSession;

  const effectiveChallenge =
    freshChallengeInUrl ||
    (canUseCookieChallenge ? loginChallengeFromCookie : null);

  useEffect(() => {
    // ── Handle the "direct registration link" case ────────────────────────────
    // When the user started the registration journey by navigating directly to
    // /email-verification (e.g. via a "Register" button on the GOL home page),
    // no OAuth2 flow was initiated and therefore no login_challenge exists in
    // the URL OR the cookie.  Submitting the login form with an empty challenge
    // would fail at the MFA step because Hydra returns 410 Gone.
    //
    // IMPORTANT — check the COOKIE too, not just the URL.
    // When the user arrives via the login page (GOL website initiates OAuth2 →
    // challenge in URL → stored in cookie → user navigates to /email-verification),
    // the challenge is no longer in the URL but IS in the cookie.  We must NOT
    // initiate a new OAuth2 flow in that case: the new flow would use our own
    // redirect_uri instead of GOL's, causing CallbackPage.js to handle the
    // callback and redirecting to account.sainsburys.co.uk/sainsburys/ui/gol
    // rather than the GOL website.
    //
    // Only initiate a fresh OAuth2 round-trip when there is genuinely no challenge
    // available anywhere (direct-link registration with no prior GOL OAuth2 context).
    if (isFromRegistration && !freshChallengeInUrl && !canUseCookieChallenge) {
      sessionStorage.setItem(
        PENDING_REG_LOGIN_KEY,
        JSON.stringify({ username, password })
      );
      initiateOAuth2Flow(missionId);
      return; // Do NOT submit the form yet — wait for the OAuth2 round-trip.
    }

    const form = document.getElementById(LOGIN_AFTER_REGISTRATION_FORM_ID);
    const submitForm = async () => {
      try {
        isIovationEnabledValue && (await getAndAssignBlackBoxToken(form));
        form.submit();
      } catch {
        form.submit();
      }
    };
    submitForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Form
      method="post"
      action={getLoginLink()}
      id={LOGIN_AFTER_REGISTRATION_FORM_ID}
    >
      {isIovationEnabledValue && (
        <input
          type="hidden"
          name={BLACK_BOX_ELEMENT_ID}
          id={BLACK_BOX_ELEMENT_ID}
          data-testid={BLACK_BOX_ELEMENT_ID}
        />
      )}
      <input type="hidden" name={CHALLENGE} value={effectiveChallenge} />
      <input type="hidden" name={USERNAME} value={username} />
      <input type="hidden" name={PASSWORD} value={password} />
      <input
        type="hidden"
        name={FROM_REGISTRATION}
        value={isFromRegistration ? "1" : "0"}
      />
    </Form>
  );
};

export default SilentLogin;
